<template lang="pug">
#episodes-tab
  spinner(v-if="settings.loading")
  div(v-else)
    b-table(
      sort-icon="chevron-up"
      :data="episodes"
      :default-sort="[filters.sort.field, filters.sort.order]"
      :default-sort-direction="filters.sort.order"
      :hoverable="true"
      :total="pagination.total"
      :current-page="pagination.page"
      :per-page="pagination.per"
      :row-class="rowClass"
      @click="open"
      @page-change="getPage"
      @sort="sort"
      backend-pagination
      backend-sorting
      paginated
    )
      template(slot-scope="props")
        b-table-column(:width="account.premium_episodes ? 90 : 62")
          .episode-meta-icons
            b-icon(:icon="props.row.video_id ? 'video' : 'video-off'" size="is-small" :class="{ 'has-text-grey':props.row.video_id, 'has-text-grey-lighter':!props.row.video_id }")
            b-icon(:icon="props.row.audio_id ? 'music' : 'music-off'" size="is-small" :class="{ 'has-text-grey':props.row.audio_id, 'has-text-grey-lighter':!props.row.audio_id }")
            b-icon(
              v-if="account.premium_episodes"
              :icon="props.row.is_premium ? 'lock' : 'lock-open-variant'" size="is-small" :class="{ 'has-text-grey':props.row.is_premium, 'has-text-grey-lighter':!props.row.is_premium }"
            )
        b-table-column(
          label="Title"
          field="title.raw"
          sortable
        )
          span.overflow {{ props.row.title }}
        b-table-column(
          label="Publish Date"
          field="pub_date"
          sortable
        )
          small {{ props.row.pub_date | date }}
        b-table-column(
          label="Audio Duration"
          field="_embedded.audio.duration"
          sortable
        )
          small {{ (props.row._embedded.audio ? props.row._embedded.audio.duration : 0) | timecode }}
        b-table-column(
          label="Speaker"
          field="_embedded.speaker.full_name.raw"
          sortable
        )
          small(v-if="props.row._embedded.speaker.full_name") {{ props.row._embedded.speaker.full_name }}
          small.has-text-grey-light(v-else) NA
        b-table-column(label="Channels")
          span(v-if="props.row._embedded.channels && props.row._embedded.channels.length > 0")
            small(
              v-for="channel in props.row._embedded.channels"
              :key="channel.id"
            ) {{ channel.title }}
          small.has-text-grey-light(v-else) NA
        b-table-column(label="Page")
          small
            a.has-text-primary(:href="`https://${account.host}/episodes/${props.row.id}`" target="_blank")
              span View
              b-icon(icon="open-in-new" size="is-small")
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import EpisodeForm from '@/components/form/EpisodeForm';
import Spinner from '@/components/Spinner';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'episodes-tab',
  components: {
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
      episodes: getters.GET_EPISODES,
      filters: getters.GET_EPISODE_FILTERS,
      mediaRefresh: getters.GET_MEDIA_REFRESH,
      mediaFilters: getters.GET_MEDIA_FILTERS,
      mediaTab: getters.GET_MEDIA_TAB,
      mediaTags: getters.GET_MEDIA_TAGS,
    }),
    esQuery() {
      const from = this.pagination.per * (this.pagination.page - 1);
      const sort = {};
      sort[this.filters.sort.field] = this.filters.sort.order;
      const payload = {
        from,
        size: this.pagination.per,
        sort: [sort],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
            ],
            should: [],
          },
        },
      };
      if (this.mediaFilters.dates) {
        payload.query.bool.must.push({
          range: {
            pub_date: {
              gte: this.mediaFilters.dates[0],
              lte: this.mediaFilters.dates[1],
            },
          },
        });
      }
      // if (this.mediaFilters.toDate) {
      //   payload.query.bool.must.push({
      //     range: {
      //       pub_date: { lte: this.mediaFilters.toDate },
      //     },
      //   });
      // }
      this.mediaTags.forEach((tag) => {
        const item = {};
        switch (tag._index) {
          case 'doxa_episodes':
            item.match = { id: tag._id };
            break;
          case 'doxa_channels':
            item.terms = { channel_ids: [tag._id] };
            break;
          case 'doxa_episode_collections':
            item.terms = { collection_ids: [tag._id] };
            break;
          case 'doxa_speakers':
            item.match = { '_embedded.speaker.id': tag._id };
            break;
          default:
        }
        if (Object.entries(item).length > 0) {
          payload.query.bool.must.push(item);
        }
      });
      return payload;
    },
  },
  created() {
    this.getPage(1);
  },
  data() {
    return {
      pagination: {
        page: 1,
        per: 26,
        total: 0,
      },
      settings: {
        loading: false,
      },
    };
  },
  methods: {
    async getPage(page, skipAnimation) {
      if (!skipAnimation) this.settings.loading = true;
      this.pagination.page = page;
      this.$router.push({ name: 'media', query: { page } });
      const response = await this.$store.dispatch(actions.DO_SEARCH_EPISODES, this.esQuery);
      this.pagination.total = response.hits.total;
      if (!skipAnimation) this.settings.loading = false;
    },
    async open(episode) {
      await this.$store.dispatch(actions.DO_GET_EPISODE, {
        id: episode.id,
        zoom: 'artwork,audio,channels,collections,video',
      });
      this.$buefy.modal.open({
        parent: this,
        component: EpisodeForm,
        hasModalCard: true,
      });
    },
    async refresh() {
      const response = await this.$store.dispatch(actions.DO_SEARCH_EPISODES, this.esQuery);
      this.$store.commit(mutations.SET_MEDIA_REFRESH, false);
      this.pagination.total = response.hits.total;
    },
    rowClass(row) {
      if (row.is_published) {
        return moment().isSameOrAfter(row.pub_date) ? 'is-published' : 'is-pending';
      }
      return 'is-draft';
    },
    sort(field, order) {
      this.$store.commit(mutations.SET_EPISODE_FILTER_FIELD, {
        key: 'sort.field',
        val: field,
      });
      this.$store.commit(mutations.SET_EPISODE_FILTER_FIELD, {
        key: 'sort.order',
        val: order,
      });
      this.getPage(1, true);
    },
  },
  watch: {
    mediaRefresh(val) {
      if (val) this.refresh();
    },
  },
};
</script>

<style lang="scss">
#episodes-tab {
  table.table {
    tr {
      td {
        small {
          // color: #7a7a7a;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/EpisodesTab.vue